import { errorToast, warningToast } from '../store/slices/toaster.slice';
import i18n from '../i18n';

// Extract { data, status } from any supported error shape:
// - axios: error.response.{data,status}
// - RTK Query rejection from `queryFulfilled`: error.error.{data,status}
// - RTK Query `.unwrap()` throw: error.{data,status}
const normalizeError = (err) => {
  if (!err || typeof err !== 'object') return { data: null, status: null };

  if (err.response) {
    return { data: err.response.data ?? null, status: err.response.status ?? null };
  }

  if (err.error && ('data' in err.error || 'status' in err.error)) {
    return { data: err.error.data ?? null, status: err.error.status ?? null };
  }

  if ('data' in err || 'status' in err) {
    return { data: err.data ?? null, status: err.status ?? null };
  }

  return { data: null, status: null };
};

export class ErrorService {
  static ERRORS = {
    SESSION_EXPIRED: 'token_not_valid',
    NOT_AUTHENTICATED: 'not_authenticated',
    UNIQUE: 'unique',
    INVALID: 'invalid',
    NO_ACTIVE_ACCOUNT: 'no_active_account',
    USER_NOT_ACTIVATED: 'user_not_activated',
    USER_ALREADY_ACTIVATED: 'user_already_activated',
    UNDECODABLE_VALUE: 'undecodable_value',
    INVALID_EMAIL_CONFIRMATION_TOKEN: 'invalid_email_confirmation_token',
    INVALID_PASSWORD_RESET_TOKEN: 'invalid_password_reset_token',
    DOES_NOT_EXIST: 'does_not_exist',
  };

  init(store) {
    this.store = store;
  }

  handleErrorToast(error, errorsMap) {
    const { data } = normalizeError(error);
    let code = data?.code;

    if (!code) {
      console.error('Error toast handler; code not found');
      return;
    }

    // TODO: this is a hack due to misunderstanding with backend that top code should be feature specific
    if (code === ErrorService.ERRORS.INVALID && data.errors?.[0]) {
      code = data.errors[0].code;
    }

    if (!errorsMap[code]) {
      this.showBackendErrors(error);
      return;
    }

    const errorTranslation = errorsMap[code];

    if (errorTranslation) {
      const toast = errorTranslation.type === 'warning' ? warningToast : errorToast;
      toast(i18n.t(errorTranslation.content), i18n.t(errorTranslation.title));
    }
  }

  showBackendErrors(error, warn = false) {
    const { data } = normalizeError(error);

    if (!data || !data.code) {
      errorToast('error');
      return;
    }

    if (data.code === ErrorService.ERRORS.SESSION_EXPIRED) {
      return;
    }

    if (data.errors?.length) {
      data.errors.forEach((item) =>
        warn ? warningToast(item.message, data.message) : errorToast(item.message, data.message)
      );
    } else if (data.message) {
      errorToast(data.message, i18n.t('common:errors.error'));
    }
  }

  displayDefault(error) {
    if (!this.store.getState().toasts.length) {
      this.showBackendErrors(error);
    }
  }

  backendValidationError = (error) => {
    const { data, status } = normalizeError(error);

    if (status === 400) {
      if (data && (data.code === 'validation_error' || data.code === 'invalid')) {
        errorToast(
          i18n.t('common:request.form.errors.backendValidationError'),
          i18n.t('common:request.form.errors.backendValidationTitle')
        );
        console.error(data.errors);
      } else {
        console.error(error);
      }
    }

    throw error;
  };
}

export const AUTHENTICATION_ERRORS = {
  [ErrorService.ERRORS.SESSION_EXPIRED]: {
    type: 'warning',
    title: 'common:errors.sessionExpiredTitle',
    content: 'common:errors.sessionExpired',
  },
  [ErrorService.ERRORS.NOT_AUTHENTICATED]: {
    type: 'warning',
    title: 'common:errors.sessionExpiredOrNoSessionTitle',
    content: 'common:errors.sessionExpiredOrNoSession',
  },
};

export const LOGIN_ERRORS = {
  [ErrorService.ERRORS.NO_ACTIVE_ACCOUNT]: {
    type: 'warning',
    title: 'common:login.unableErrorTitle',
    content: 'common:login.unableErrorContent',
  },
  [ErrorService.ERRORS.INVALID]: {
    type: 'warning',
    title: 'common:login.unableErrorTitle',
    content: 'common:login.wrongUser',
  },
  [ErrorService.ERRORS.USER_NOT_ACTIVATED]: {
    type: 'warning',
    title: 'common:login.userNotActivatedTitle',
    content: 'common:login.userNotActivatedContent',
  },
};

export const REGISTRATION_ERRORS = {
  [ErrorService.ERRORS.UNIQUE]: {
    type: 'warning',
    title: 'common:register.errors.registerErrorUniqueTitle',
    content: 'common:register.errors.registerErrorUniqueContent',
  },
};

export default new ErrorService();
